import { getAuth } from 'firebase/auth';
import { httpsCallable, type Functions } from 'firebase/functions';

type CheckoutResult = { orderId: string; checkoutUrl: string; sessionId: string };
const pending = new Map<string, Promise<{ data: CheckoutResult }>>();
const requestIds = new Map<string, string>();

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      .filter(([, v]) => v !== undefined).map(([k, v]) => [k, canonical(v)]));
  }
  return value;
}

/** Keep the same checkout intent across double clicks, navigation and network retries. */
export function startCartCheckout(functions: Functions, payload: Record<string, unknown>) {
  const userId = getAuth(functions.app).currentUser?.uid;
  if (!userId) return Promise.reject(new Error('Please sign in before checkout.'));
  const intent = JSON.stringify(canonical({ userId, payload }));
  const existing = pending.get(intent);
  if (existing) return existing;

  const promise = (async () => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(intent));
    const key = 'drx:cart-checkout:' + Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('');
    let requestId = requestIds.get(key);
    try { requestId ||= sessionStorage.getItem(key) || undefined; } catch { /* Storage can be disabled. */ }
    requestId ||= crypto.randomUUID();
    requestIds.set(key, requestId);
    try { sessionStorage.setItem(key, requestId); } catch { /* In-memory retries still reuse the intent. */ }
    try {
      return await httpsCallable<Record<string, unknown>, CheckoutResult>(functions, 'processCartCheckout')({ ...payload, requestId });
    } catch (error) {
      // Only a confirmed expired session permits a new attempt. An unknown
      // network outcome must retain its key to avoid creating a second checkout.
      if ((error as { details?: { reason?: string } })?.details?.reason === 'checkout_expired') {
        requestIds.delete(key);
        try { sessionStorage.removeItem(key); } catch { /* No persistent storage. */ }
      }
      throw error;
    }
  })().finally(() => pending.delete(intent));
  pending.set(intent, promise);
  return promise;
}
